/**
 * <cba-header> — the site header's behaviour (sections/cba-header; plan 6.1g:
 * were three inline scripts in the section).
 *
 *   <cba-header class="cba-header" data-cba-header data-cart-url="{{ routes.cart_url }}">
 *
 * - The cart buttons ([data-cba-open-cart]) open the cart drawer, or go to the
 *   cart page when the drawer is not on the page (the cart page itself).
 * - Shop menu (desktop): opens on hover with a short delay, and on a click,
 *   Enter or Space (WAI-ARIA APG disclosure navigation; DESIGN-PLAN 2.6).
 *   Tab is steered Shop -> the panel's links -> the rest of the header.
 *   Escape, a click outside, another overlay opening (cba:overlay:open) or the
 *   viewport dropping below 1024 px closes it.
 * - Cart badge: the count Shopify's standard cart events carry
 *   (shopify:cart:lines-update and the others; DESIGN-PLAN 7.G2), whoever
 *   changed the cart. While the added-to-cart popup flies it is held
 *   (cba:cart:badge-hold), and shown at its landing (cba:cart:badge-sync). Read from /cart.js only on
 *   load and when the page comes back from the back-forward cache, where the
 *   server's number may be old.
 * - Every listener goes when the element leaves the page (a section reload in
 *   the theme editor), and so does the backdrop it moved to <body>.
 */
if (!customElements.get('cba-header')) {
  const OPEN_DELAY = 60;
  const CLOSE_DELAY = 180;
  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

  class CbaHeader extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      const { signal } = this.abort;
      this.addEventListener('click', (e) => {
        const trigger = e.target.closest('[data-cba-open-cart]');
        if (!trigger) return;
        const drawer = document.querySelector('cba-cart-drawer');
        if (drawer && typeof drawer.open === 'function') drawer.open(trigger);
        else window.location.href = this.dataset.cartUrl;
      }, { signal });
      this.initShopMenu(signal);
      this.initCartBadge(signal);
    }

    disconnectedCallback() {
      if (this.abort) this.abort.abort();
      this.clearTimers();
      if (this.backdrop && this.backdrop.parentNode === document.body) this.backdrop.remove();
    }

    clearTimers() {
      clearTimeout(this.openTimer);
      clearTimeout(this.closeTimer);
      this.openTimer = null;
      this.closeTimer = null;
    }

    initShopMenu(signal) {
      const header = this;
      const shopEl = header.querySelector('[data-cba-shop]');
      const panel = header.querySelector('[data-cba-shop-panel]');
      const menu = header.querySelector('.cba-shop-menu');
      const btn = shopEl && shopEl.querySelector('.cba-header__shop-btn');
      if (!shopEl || !panel || !menu || !btn) return;

      // Hoist the backdrop out of the header into <body>. Any ancestor with a
      // transform / filter / will-change / contain makes position:fixed anchor
      // to that ancestor instead of the viewport, which silently breaks
      // fullscreen overlays. Moving it to body guarantees viewport anchoring.
      const backdrop = this.backdrop || header.querySelector('[data-cba-shop-backdrop]');
      this.backdrop = backdrop;
      if (backdrop && backdrop.parentNode !== document.body) document.body.appendChild(backdrop);

      const isOpen = () => header.getAttribute('data-cba-shop-open') === '1';

      // Position the panel at the bottom of the header bar, and the backdrop
      // at the bottom of the panel, so the dark overlay only dims the page
      // below the dropdown, never the dropdown itself.
      const syncTopOffset = () => {
        const bar = header.querySelector('.cba-header__bar');
        const headerBottom = bar ? bar.getBoundingClientRect().bottom : 56;
        menu.style.top = headerBottom + 'px';
        if (backdrop) {
          // offsetHeight is the panel's natural height (unaffected by
          // transform), so this is stable while the panel slides in.
          backdrop.style.top = (headerBottom + panel.offsetHeight) + 'px';
        }
      };

      // A menu the pointer opened closes when the pointer leaves it; one opened
      // by a click, Enter or Space stays until Escape, a click outside, focus
      // leaving or another click (logic audit S2.1: opened by the keyboard
      // while the mouse rested on the page, it closed itself after ~200 ms,
      // because the backdrop appeared under the cursor and its mouseenter
      // closed it). A click on a menu the pointer opened keeps it open
      // (S2.2: it used to toggle it shut). Horizon's header-menu does the same.
      let byPointer = false;
      const setOpen = (open) => {
        if (!open) byPointer = false;
        if (open) {
          /* One overlay at a time (DESIGN-RULES §6): search and cart close. */
          if (!isOpen()) document.dispatchEvent(new CustomEvent('cba:overlay:open', { detail: 'shop' }));
          syncTopOffset();
          header.setAttribute('data-cba-shop-open', '1');
        } else {
          header.removeAttribute('data-cba-shop-open');
        }
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
        menu.setAttribute('aria-hidden', open ? 'false' : 'true');
        if (backdrop) backdrop.classList.toggle('is-open', open);
      };
      const openMenu = () => { this.clearTimers(); this.openTimer = setTimeout(() => { if (!isOpen()) byPointer = true; setOpen(true); }, OPEN_DELAY); };
      const closeMenu = () => { if (!byPointer) return; this.clearTimers(); this.closeTimer = setTimeout(() => setOpen(false), CLOSE_DELAY); };
      const clearTimers = () => this.clearTimers();

      // Hover intent on the Shop button and the panel (the delay bridges the
      // gap between them, so the menu does not close while the cursor crosses).
      shopEl.addEventListener('mouseenter', openMenu, { signal });
      shopEl.addEventListener('mouseleave', closeMenu, { signal });
      panel.addEventListener('mouseenter', clearTimers, { signal });
      panel.addEventListener('mouseleave', closeMenu, { signal });
      // A click on the dim backdrop closes the menu; the cursor moving onto it
      // closes only a menu the pointer opened.
      if (backdrop) {
        backdrop.addEventListener('click', () => setOpen(false), { signal });
        backdrop.addEventListener('mouseenter', closeMenu, { signal });
      }

      // Keyboard (WAI-ARIA APG, disclosure navigation; DESIGN-PLAN 2.6): the
      // button toggles with Enter, Space or a click; focus alone does not open
      // it (it used to, and the open menu and its backdrop then stayed over the
      // page while a keyboard user tabbed on). The panel sits after the header
      // in the DOM, so Tab is steered: Shop -> the panel's links -> search,
      // cart and sign in. Focus leaving the button and the panel closes it.
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        this.clearTimers();
        if (isOpen() && byPointer) { byPointer = false; return; }
        setOpen(!isOpen());
      }, { signal });

      const shown = (el) => (el.checkVisibility ? el.checkVisibility({ visibilityProperty: true }) : el.offsetParent !== null);
      const panelItems = () => Array.from(menu.querySelectorAll(FOCUSABLE)).filter(shown);
      const afterButton = () => {
        const all = Array.from(header.querySelectorAll(FOCUSABLE)).filter((el) => shown(el) && !menu.contains(el));
        return all[all.indexOf(btn) + 1] || null;
      };
      btn.addEventListener('keydown', (e) => {
        if (e.key !== 'Tab' || e.shiftKey || !isOpen()) return;
        const items = panelItems();
        if (!items.length) return;
        e.preventDefault();
        items[0].focus();
      }, { signal });
      menu.addEventListener('keydown', (e) => {
        if (e.key !== 'Tab' || !isOpen()) return;
        const items = panelItems();
        if (!items.length) return;
        if (e.shiftKey && document.activeElement === items[0]) {
          e.preventDefault();
          btn.focus();
        } else if (!e.shiftKey && document.activeElement === items[items.length - 1]) {
          e.preventDefault();
          const next = afterButton();
          this.clearTimers();
          setOpen(false);
          if (next) next.focus();
        }
      }, { signal });
      const onFocusOut = (e) => {
        const to = e.relatedTarget;
        if (!to || !isOpen() || shopEl.contains(to) || menu.contains(to)) return;
        this.clearTimers();
        setOpen(false);
      };
      shopEl.addEventListener('focusout', onFocusOut, { signal });
      menu.addEventListener('focusout', onFocusOut, { signal });

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && isOpen()) {
          setOpen(false);
          btn.focus();
        }
      }, { signal });
      document.addEventListener('click', (e) => {
        if (!header.contains(e.target) && isOpen()) setOpen(false);
      }, { signal });
      document.addEventListener('cba:overlay:open', (e) => {
        if (e.detail !== 'shop' && isOpen()) { this.clearTimers(); setOpen(false); }
      }, { signal });

      // Force-close when the viewport drops below the desktop breakpoint.
      const mq = window.matchMedia('(max-width: 1023px)');
      if (mq.matches) setOpen(false);
      if (mq.addEventListener) mq.addEventListener('change', (ev) => { if (ev.matches) setOpen(false); }, { signal });
    }

    initCartBadge(signal) {
      const badges = Array.from(this.querySelectorAll('[data-cba-cart-badge]'));
      if (!badges.length) return;

      const plural = new Intl.PluralRules('en');
      const renderBadge = (count) => {
        const c = Math.max(0, parseInt(count, 10) || 0);
        const text = c > 99 ? '99+' : String(c);
        /* The cart link's name says the count, "Cart, 2 items" (S1.1). */
        const label = (plural.select(c) === 'one' ? this.dataset.labelOne : this.dataset.labelOther) || '';
        if (label) this.querySelectorAll('[data-cba-cart-label]').forEach((link) => link.setAttribute('aria-label', label.replace('%N%', c)));
        badges.forEach((badge) => {
          badge.setAttribute('data-cba-cart-count', String(c));
          const t = badge.querySelector('[data-cba-cart-badge-text]');
          if (t) t.textContent = text;
        });
      };

      /* The count from a cart change; held while the added-to-cart popup
         flies (<cba-cart-feedback> asks for the hold and releases it on
         landing). */
      let holding = false;
      let held = null;
      const showCount = (count) => {
        if (holding) { held = count; return; }
        held = null;
        renderBadge(count);
      };
      document.addEventListener('cba:cart:badge-hold', () => { holding = true; }, { signal });
      ['shopify:cart:lines-update', 'shopify:cart:discount-update', 'shopify:cart:note-update', 'shopify:cart:attributes-update'].forEach((name) => {
        document.addEventListener(name, (e) => {
          if (!e.promise) return;
          e.promise.then((result) => {
            if (!signal.aborted && result && result.cart) showCount(result.cart.totalQuantity);
          }, () => { /* failed: the count did not change */ });
        }, { signal });
      });
      document.addEventListener('cba:cart:badge-sync', () => {
        holding = false;
        if (held !== null) renderBadge(held);
        held = null;
      }, { signal });

      const fetchCount = () => {
        fetch(this.dataset.cartUrl + '.js', { cache: 'no-store', headers: { Accept: 'application/json' } })
          .then((r) => r.json())
          .then((cart) => { if (!signal.aborted) showCount(cart.item_count || 0); })
          .catch(() => { /* keep what the server rendered */ });
      };
      /* The server-rendered count can be old after the back button
         (back-forward cache) or a cached page. */
      window.addEventListener('pageshow', (e) => { if (e.persisted) fetchCount(); }, { signal });
      fetchCount();
    }
  }

  customElements.define('cba-header', CbaHeader);
}
